import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Spinner,
  Alert,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getReleases,
  createRelease,
  updateRelease,
  publishRelease,
  deleteRelease,
  uploadReleaseVideo,
  deleteReleaseVideo,
} from "../releasesApi";
import "../Components/Accounts/acc-list.css";

const STATUS_BADGES = {
  Draft: "secondary",
  Released: "success",
};

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};
const quillFormats = ["bold", "italic", "underline", "list", "bullet", "align"];

function isSystemAdminFromToken() {
  try {
    const token = sessionStorage.getItem("user");
    if (!token) return false;
    const decoded = jwtDecode(token);
    const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
    const roles = [
      ...asArray(decoded?.role),
      ...asArray(decoded?.permissions),
    ];
    return roles.includes("SystemAdmin");
  } catch {
    return false;
  }
}

export function Releases() {
  const isSystemAdmin = useMemo(isSystemAdminFromToken, []);

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create/Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null); // null = create
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // Video upload state (inside the edit modal)
  const [videoFile, setVideoFile] = useState(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [fileInputKey, setFileInputKey] = useState(0);

  // Release / delete confirmation modal
  const [confirmAction, setConfirmAction] = useState(null); // { mode: "release"|"delete", release }
  const [confirming, setConfirming] = useState(false);

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getReleases();
      setReleases(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching releases:", err);
      setError("Failed to load releases.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin) fetchReleases();
  }, [isSystemAdmin, fetchReleases]);

  function openCreateModal() {
    setEditingRelease(null);
    setFormName("");
    setFormDescription("");
    setFormError("");
    setVideoFile(null);
    setVideoTitle("");
    setUploadPercent(0);
    setFileInputKey((k) => k + 1);
    setShowEditModal(true);
  }

  function openEditModal(release) {
    setEditingRelease(release);
    setFormName(release.name || "");
    setFormDescription(release.descriptionHtml || "");
    setFormError("");
    setVideoFile(null);
    setVideoTitle("");
    setUploadPercent(0);
    setFileInputKey((k) => k + 1);
    setShowEditModal(true);
  }

  function closeEditModal() {
    if (saving || uploading) return;
    setShowEditModal(false);
    setEditingRelease(null);
  }

  async function handleSave() {
    if (!formName.trim()) {
      setFormError("Name is required.");
      return;
    }
    setFormError("");
    setSaving(true);
    try {
      if (editingRelease?._id) {
        await updateRelease(editingRelease._id, {
          name: formName.trim(),
          description: formDescription,
        });
        setShowEditModal(false);
        setEditingRelease(null);
      } else {
        // Keep the modal open after creating so videos can be uploaded
        // straight away — uploads need the release id.
        const created = await createRelease({
          name: formName.trim(),
          description: formDescription,
        });
        setEditingRelease(created);
      }
      await fetchReleases();
    } catch (err) {
      console.error("Error saving release:", err);
      setFormError(err.response?.data?.message || "Failed to save release.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUploadVideo() {
    if (!editingRelease?._id || !videoFile) return;
    setFormError("");
    setUploading(true);
    setUploadPercent(0);
    try {
      const updated = await uploadReleaseVideo(
        editingRelease._id,
        videoFile,
        videoTitle.trim(),
        (e) => {
          if (e.total) setUploadPercent(Math.round((e.loaded / e.total) * 100));
        }
      );
      setEditingRelease(updated);
      setVideoFile(null);
      setVideoTitle("");
      setFileInputKey((k) => k + 1);
      await fetchReleases();
    } catch (err) {
      console.error("Error uploading video:", err);
      setFormError(err.response?.data?.message || "Failed to upload video.");
    } finally {
      setUploading(false);
      setUploadPercent(0);
    }
  }

  async function handleDeleteVideo(blobName) {
    if (!editingRelease?._id) return;
    setFormError("");
    try {
      await deleteReleaseVideo(editingRelease._id, blobName);
      setEditingRelease((prev) => ({
        ...prev,
        videos: (prev.videos || []).filter((v) => v.blobName !== blobName),
      }));
      await fetchReleases();
    } catch (err) {
      console.error("Error deleting video:", err);
      setFormError(err.response?.data?.message || "Failed to remove video.");
    }
  }

  async function handleConfirm() {
    if (!confirmAction) return;
    setConfirming(true);
    try {
      if (confirmAction.mode === "release") {
        await publishRelease(confirmAction.release._id);
      } else {
        await deleteRelease(confirmAction.release._id);
      }
      setConfirmAction(null);
      await fetchReleases();
    } catch (err) {
      console.error("Error updating release:", err);
      setError(err.response?.data?.message || "Action failed. Please try again.");
      setConfirmAction(null);
    } finally {
      setConfirming(false);
    }
  }

  if (!isSystemAdmin) {
    return (
      <Container fluid className="pt-2" style={{ marginTop: "50px" }}>
        <Alert variant="danger">You are not authorised to view this page.</Alert>
      </Container>
    );
  }

  const renderActions = (release, inDropdown = false) => {
    if (inDropdown) {
      return (
        <>
          <Dropdown.Item onClick={() => openEditModal(release)}>
            <i className="fa-solid fa-pen me-2" />
            Edit
          </Dropdown.Item>
          {release.status === "Draft" && (
            <Dropdown.Item
              onClick={() => setConfirmAction({ mode: "release", release })}
            >
              <i className="fa-solid fa-rocket me-2" />
              Release
            </Dropdown.Item>
          )}
          <Dropdown.Item
            className="text-danger"
            onClick={() => setConfirmAction({ mode: "delete", release })}
          >
            <i className="fa-solid fa-trash me-2" />
            Delete
          </Dropdown.Item>
        </>
      );
    }
    return (
      <div className="d-flex justify-content-center gap-2">
        <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
          <Button
            variant="link"
            className="text-primary text-decoration-none p-0"
            style={{ width: 24, height: 24 }}
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(release);
            }}
          >
            <i className="fa-solid fa-pen" />
          </Button>
        </OverlayTrigger>
        {release.status === "Draft" && (
          <OverlayTrigger placement="top" overlay={<Tooltip>Release</Tooltip>}>
            <Button
              variant="link"
              className="text-primary text-decoration-none p-0"
              style={{ width: 24, height: 24 }}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmAction({ mode: "release", release });
              }}
            >
              <i className="fa-solid fa-rocket" />
            </Button>
          </OverlayTrigger>
        )}
        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
          <Button
            variant="link"
            className="text-danger text-decoration-none p-0"
            style={{ width: 24, height: 24 }}
            onClick={(e) => {
              e.stopPropagation();
              setConfirmAction({ mode: "delete", release });
            }}
          >
            <i className="fa-solid fa-trash" />
          </Button>
        </OverlayTrigger>
      </div>
    );
  };

  return (
    <Container fluid className="pt-2 acc-list" style={{ marginTop: "50px" }}>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <h5 className="text-primary mb-0">Releases</h5>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="fa-solid fa-plus me-2" />
          Create Release
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading releases…</p>
        </div>
      ) : releases.length === 0 ? (
        <p className="text-center py-4">No releases found.</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="d-none d-lg-block">
            <Table bordered hover responsive>
              <thead className="text-center">
                <tr>
                  <th className="text-center align-middle text-primary">Name</th>
                  <th className="text-center align-middle text-primary">Status</th>
                  <th className="text-center align-middle text-primary">Videos</th>
                  <th className="text-center align-middle text-primary">Created</th>
                  <th className="text-center align-middle text-primary">Released</th>
                  <th className="text-center align-middle text-primary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {releases.map((release) => (
                  <tr
                    key={release._id}
                    style={{ cursor: "pointer" }}
                    onClick={() => openEditModal(release)}
                  >
                    <td className="align-middle">{release.name}</td>
                    <td className="align-middle text-center">
                      <Badge bg={STATUS_BADGES[release.status] || "secondary"}>
                        {release.status}
                      </Badge>
                    </td>
                    <td className="align-middle text-center">
                      {(release.videos || []).length}
                    </td>
                    <td className="align-middle text-center">
                      {release.createdAt
                        ? new Date(release.createdAt).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="align-middle text-center">
                      {release.releasedAt
                        ? new Date(release.releasedAt).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="align-middle">{renderActions(release)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="d-lg-none">
            {releases.map((release) => (
              <div className="acc-card" key={release._id}>
                <div className="acc-card-top">
                  <span className="acc-card-id">{release.name}</span>
                  <Badge bg={STATUS_BADGES[release.status] || "secondary"}>
                    {release.status}
                  </Badge>
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      variant="link"
                      className="acc-card-kebab text-primary text-decoration-none p-0 border-0"
                      aria-label="Release actions"
                    >
                      <i className="fa-solid fa-ellipsis-vertical" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end" className="shadow-sm">
                      {renderActions(release, true)}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className="acc-card-body">
                  <div className="acc-card-field">
                    <span className="acc-card-label">Videos</span>
                    <span className="acc-card-value">
                      {(release.videos || []).length}
                    </span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Created</span>
                    <span className="acc-card-value">
                      {release.createdAt
                        ? new Date(release.createdAt).toLocaleDateString("en-GB")
                        : "-"}
                    </span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Released</span>
                    <span className="acc-card-value">
                      {release.releasedAt
                        ? new Date(release.releasedAt).toLocaleDateString("en-GB")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create / Edit modal */}
      <Modal show={showEditModal} onHide={closeEditModal} centered size="lg" scrollable>
        <Modal.Header
          closeButton={!saving && !uploading}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            {editingRelease?._id
              ? `Edit Release — ${editingRelease.name}`
              : "Create Release"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Version 24.0 — Receipts overhaul"
              maxLength={200}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <ReactQuill
              theme="snow"
              value={formDescription}
              onChange={setFormDescription}
              modules={quillModules}
              formats={quillFormats}
              placeholder="What changed in this release?"
              style={{ height: "200px", marginBottom: "3rem" }}
            />
          </Form.Group>

          {editingRelease?._id ? (
            <>
              <h6 className="mb-2 text-primary fw-bold">Videos</h6>
              {(editingRelease.videos || []).length === 0 ? (
                <p className="small text-muted">No videos uploaded yet.</p>
              ) : (
                <Table bordered size="sm" className="mb-3">
                  <thead className="text-center">
                    <tr>
                      <th className="text-center align-middle text-primary">Title</th>
                      <th className="text-center align-middle text-primary">Uploaded</th>
                      <th className="text-center align-middle text-primary" style={{ width: 60 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {(editingRelease.videos || []).map((video) => (
                      <tr key={video.blobName}>
                        <td className="align-middle">{video.title}</td>
                        <td className="align-middle text-center">
                          {video.uploadedAt
                            ? new Date(video.uploadedAt).toLocaleDateString("en-GB")
                            : "-"}
                        </td>
                        <td className="align-middle text-center">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Remove video</Tooltip>}
                          >
                            <Button
                              variant="link"
                              className="text-danger text-decoration-none p-0"
                              style={{ width: 24, height: 24 }}
                              onClick={() => handleDeleteVideo(video.blobName)}
                            >
                              <i className="fa-solid fa-trash" />
                            </Button>
                          </OverlayTrigger>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <Row className="g-2 align-items-end">
                <Col xs={12} md={5}>
                  <Form.Group>
                    <Form.Label>Video title</Form.Label>
                    <Form.Control
                      type="text"
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      placeholder="e.g. New receipts modal walkthrough"
                      maxLength={200}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Group>
                    <Form.Label>Video file</Form.Label>
                    <Form.Control
                      key={fileInputKey}
                      type="file"
                      accept="video/*"
                      onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-100"
                    disabled={!videoFile || uploading}
                    onClick={handleUploadVideo}
                  >
                    {uploading ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        {uploadPercent}%
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-upload me-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <div className="posted-invoice-info">
              <small>
                Save the release first — videos can be uploaded once it exists.
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || uploading}
            style={{ minWidth: 120 }}
          >
            {saving ? (
              <Spinner size="sm" animation="border" />
            ) : editingRelease?._id ? (
              "Save"
            ) : (
              "Create"
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={closeEditModal}
            disabled={saving || uploading}
            style={{ minWidth: 120 }}
          >
            {editingRelease?._id ? "Close" : "Cancel"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Release / Delete confirmation */}
      <Modal
        show={!!confirmAction}
        onHide={() => !confirming && setConfirmAction(null)}
        centered
      >
        <Modal.Header
          closeButton={!confirming}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            {confirmAction?.mode === "release"
              ? `Release — ${confirmAction?.release?.name}`
              : `Delete Release — ${confirmAction?.release?.name}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction?.mode === "release" ? (
            <Alert variant="warning" className="mb-0">
              This will make the release visible to <strong>all users</strong> —
              each user will see the release notes modal on their next login.
              This cannot be undone.
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-0">
              This will <strong>permanently delete</strong> this release and all
              of its uploaded videos.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={confirmAction?.mode === "release" ? "primary" : "danger"}
            onClick={handleConfirm}
            disabled={confirming}
            style={{ minWidth: 140 }}
          >
            {confirming ? (
              <Spinner size="sm" animation="border" />
            ) : confirmAction?.mode === "release" ? (
              <>
                <i className="fa-solid fa-rocket me-2" />
                Release
              </>
            ) : (
              <>
                <i className="fa-solid fa-trash me-2" />
                Delete
              </>
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmAction(null)}
            disabled={confirming}
            style={{ minWidth: 140 }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
